/**
 * Header search: infinite "Search by [Collection]..." typing animation.
 * Uses a plain controller (not only customElements) so theme hot-reload always restarts cleanly.
 */

class SearchPlaceholderTyper {
  constructor(root) {
    this.root = root;
    this.prefix = root.getAttribute('data-prefix') || 'Search by ';
    this.suffix = root.getAttribute('data-suffix') || '...';
    this.typeSpeed = parseInt(root.getAttribute('data-type-speed'), 10) || 70;
    this.deleteSpeed = parseInt(root.getAttribute('data-delete-speed'), 10) || 40;
    this.pauseMs = parseInt(root.getAttribute('data-pause'), 10) || 1400;
    this.names = this._readNames();
    this.textEl =
      root.querySelector('.search-placeholder-type__text') || this._createTextEl();
    this.input = this._resolveInput();
    this.termIndex = 0;
    this.charIndex = 0;
    this.isDeleting = false;
    this.isPaused = false;
    this._timer = null;
    this._bound = false;
  }

  _readNames() {
    const jsonEl = this.root.querySelector('[data-header-search-collections]');
    if (jsonEl) {
      try {
        const parsed = JSON.parse(jsonEl.textContent.trim());
        if (Array.isArray(parsed)) {
          return parsed.map((n) => String(n).trim()).filter(Boolean);
        }
      } catch (e) {
        /* ignore */
      }
    }

    return (this.root.getAttribute('data-names') || '')
      .split('|')
      .map((n) => n.trim())
      .filter(Boolean);
  }

  _resolveInput() {
    const id = this.root.getAttribute('data-input');
    return id ? document.getElementById(id) : null;
  }

  _createTextEl() {
    const el = document.createElement('span');
    el.className = 'search-placeholder-type__text';
    this.root.appendChild(el);
    return el;
  }

  _fullTerm(index = this.termIndex) {
    return `${this.prefix}${this.names[index] || ''}${this.suffix}`;
  }

  _render(length) {
    const name = this.names[this.termIndex] || '';
    const slice = this._fullTerm().slice(0, Math.max(0, length));
    const prefixLen = this.prefix.length;
    const nameLen = name.length;

    this.textEl.replaceChildren();

    if (slice.length > 0) {
      const prefixPart = document.createElement('span');
      prefixPart.className = 'search-placeholder-type__prefix';
      prefixPart.textContent = slice.slice(0, Math.min(slice.length, prefixLen));
      this.textEl.appendChild(prefixPart);
    }

    if (slice.length > prefixLen) {
      const namePart = document.createElement('strong');
      namePart.className = 'search-placeholder-type__name';
      namePart.textContent = slice.slice(prefixLen, Math.min(slice.length, prefixLen + nameLen));
      this.textEl.appendChild(namePart);
    }

    if (slice.length > prefixLen + nameLen) {
      const suffixPart = document.createElement('span');
      suffixPart.className = 'search-placeholder-type__suffix';
      suffixPart.textContent = slice.slice(prefixLen + nameLen);
      this.textEl.appendChild(suffixPart);
    }
  }

  bindInput() {
    if (!this.input || this._bound) return;

    this._onInput = () => this.syncFromInput();
    this._onFocus = () => this.pause();
    this._onBlur = () => {
      window.setTimeout(() => {
        if (!this.input.value.length && !this.input.matches(':focus')) this.resume();
      }, 100);
    };
    this._onReset = () => window.requestAnimationFrame(() => this.syncFromInput());

    this.input.addEventListener('input', this._onInput);
    this.input.addEventListener('focus', this._onFocus);
    this.input.addEventListener('blur', this._onBlur);
    this.input.form?.addEventListener('reset', this._onReset);
    this._bound = true;
  }

  unbindInput() {
    if (!this.input || !this._bound) return;
    this.input.removeEventListener('input', this._onInput);
    this.input.removeEventListener('focus', this._onFocus);
    this.input.removeEventListener('blur', this._onBlur);
    this.input.form?.removeEventListener('reset', this._onReset);
    this._bound = false;
  }

  syncFromInput() {
    if (!this.input) return;
    if (this.input.value.length > 0) this.pause();
    else if (!this.input.matches(':focus')) this.resume();
  }

  pause() {
    this.isPaused = true;
    this.root.classList.add('is-hidden');
  }

  resume() {
    if (this.input && this.input.value.length > 0) return;
    this.isPaused = false;
    this.root.classList.remove('is-hidden');
    if (!this._timer) this._schedule(0);
  }

  start() {
    this.stop();
    this.bindInput();

    if (!this.names.length) return;

    this.termIndex = 0;
    this.charIndex = 0;
    this.isDeleting = false;
    this.isPaused = false;
    this.root.classList.remove('is-hidden');

    // Always begin from an empty string so typing is visible immediately
    this._render(0);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this._render(this._fullTerm(0).length);
      return;
    }

    if (this.input && (this.input.value.length > 0 || this.input.matches(':focus'))) {
      this.pause();
      return;
    }

    this._schedule(this.typeSpeed);
  }

  stop() {
    window.clearTimeout(this._timer);
    this._timer = null;
  }

  destroy() {
    this.stop();
    this.unbindInput();
  }

  _schedule(delay) {
    window.clearTimeout(this._timer);
    this._timer = window.setTimeout(() => this._tick(), delay);
  }

  _tick() {
    this._timer = null;
    if (!this.names.length) return;

    if (this.isPaused) {
      this._schedule(200);
      return;
    }

    const current = this._fullTerm();

    if (!this.isDeleting) {
      this.charIndex = Math.min(this.charIndex + 1, current.length);
      this._render(this.charIndex);

      if (this.charIndex >= current.length) {
        this.isDeleting = true;
        this._schedule(this.pauseMs);
        return;
      }

      this._schedule(this.typeSpeed);
      return;
    }

    this.charIndex = Math.max(this.charIndex - 1, 0);
    this._render(this.charIndex);

    if (this.charIndex <= 0) {
      this.isDeleting = false;
      this.termIndex = (this.termIndex + 1) % this.names.length;
      this._schedule(320);
      return;
    }

    this._schedule(this.deleteSpeed);
  }
}

class SearchPlaceholderType extends HTMLElement {
  connectedCallback() {
    // Re-create typer every connect so hot-reload / section morph always animates
    this._typer?.destroy();
    this._typer = new SearchPlaceholderTyper(this);
    this._typer.start();
  }

  disconnectedCallback() {
    this._typer?.destroy();
    this._typer = null;
  }

  pause() {
    this._typer?.pause();
  }

  resume() {
    this._typer?.resume();
  }
}

class HeaderInlineSearchController {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('form');
    this.input = root.querySelector('.header-search__input');
    this.dropdown = root.querySelector('[data-header-search-dropdown]');
    this.staticPanel = root.querySelector('[data-header-search-static]');
    this.searchComponent = root.querySelector('predictive-search, search-form');
    this.placeholder = root.querySelector('search-placeholder-type');
    this.clearButton = root.querySelector('.header-search__clear');

    if (!this.input || !this.dropdown) return;

    this._onInputFocus = () => this.onInputFocus();
    this._onInputClick = () => this.onInputFocus();
    this._onDocumentClick = (event) => this.onDocumentClick(event);
    this._onInputInput = () => this.onInputChange();
    this._onMutation = () => this.syncPredictiveState();

    this.input.addEventListener('focus', this._onInputFocus);
    this.input.addEventListener('click', this._onInputClick);
    this.input.addEventListener('input', this._onInputInput);
    document.addEventListener('click', this._onDocumentClick);
    this.toggleClearButton();

    if (this.searchComponent) {
      this._observer = new MutationObserver(this._onMutation);
      this._observer.observe(this.searchComponent, {
        attributes: true,
        attributeFilter: ['open', 'loading', 'results'],
      });
    }
  }

  destroy() {
    this.input?.removeEventListener('focus', this._onInputFocus);
    this.input?.removeEventListener('click', this._onInputClick);
    this.input?.removeEventListener('input', this._onInputInput);
    document.removeEventListener('click', this._onDocumentClick);
    this._observer?.disconnect();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onInputFocus() {
    this.placeholder?.pause();

    if (!this.getQuery().length) {
      this.openStatic();
      return;
    }

    if (this.searchComponent?.getAttribute('results') === 'true') {
      this.openDropdown();
      this.hideStatic();
    }
  }

  onInputChange() {
    this.toggleClearButton();

    if (!this.getQuery().length) {
      this.searchComponent?.close?.(false);
      if (this.input.matches(':focus')) {
        this.openStatic();
      } else {
        this.closeDropdown(false);
      }
      return;
    }

    if (this.searchComponent?.tagName === 'PREDICTIVE-SEARCH') {
      this.hideStatic();
      this.openDropdown();
      return;
    }

    this.closeDropdown(false);
  }

  onDocumentClick(event) {
    if (this.root.contains(event.target)) return;
    this.closeDropdown(false);
  }

  syncPredictiveState() {
    const isPredictiveOpen =
      this.searchComponent?.hasAttribute('open') || this.searchComponent?.hasAttribute('loading');

    if (isPredictiveOpen) {
      this.openDropdown();
      this.hideStatic();
      return;
    }

    if (this.getQuery().length && this.input.matches(':focus')) {
      this.openDropdown();
      this.hideStatic();
      return;
    }

    if (!this.getQuery().length && this.input.matches(':focus')) {
      this.openStatic();
      return;
    }

    this.closeDropdown(false);
  }

  toggleClearButton() {
    if (!this.clearButton) return;
    this.clearButton.classList.toggle('hidden', !this.input.value.length);
  }

  openDropdown() {
    this.dropdown.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');

    window.requestAnimationFrame(() => {
      this.dropdown.classList.add('is-open');
    });
  }

  openStatic() {
    this.openDropdown();
    this.staticPanel?.removeAttribute('hidden');
  }

  hideStatic() {
    this.staticPanel?.setAttribute('hidden', '');
  }

  closeDropdown(clearInput = false) {
    if (clearInput) {
      this.input.value = '';
    }

    this.searchComponent?.close?.(false);
    this.dropdown.classList.remove('is-open');
    this.dropdown.hidden = true;
    this.staticPanel?.setAttribute('hidden', '');
    this.input.setAttribute('aria-expanded', 'false');
    this.toggleClearButton();

    if (!this.getQuery().length && !this.input.matches(':focus')) {
      this.placeholder?.resume();
    }
  }
}

function defineSearchPlaceholder() {
  if (!customElements.get('search-placeholder-type')) {
    customElements.define('search-placeholder-type', SearchPlaceholderType);
  }
}

function initHeaderInlineSearch() {
  defineSearchPlaceholder();

  document.querySelectorAll('.header__search--inline').forEach((root) => {
    if (root._headerInlineSearch) {
      root._headerInlineSearch.destroy?.();
    }
    root._headerInlineSearch = new HeaderInlineSearchController(root);
  });

  // Force (re)start typers — works even when custom element class was defined earlier
  document.querySelectorAll('search-placeholder-type').forEach((el) => {
    if (el._typer) {
      el._typer.destroy();
    }
    el._typer = new SearchPlaceholderTyper(el);
    el._typer.start();
  });
}

defineSearchPlaceholder();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderInlineSearch);
} else {
  initHeaderInlineSearch();
}

document.addEventListener('shopify:section:load', initHeaderInlineSearch);
